import { useSelector } from 'react-redux'
import { createSelector } from 'reselect'
import { RegionalPlanApprovalStatus } from 'src/constants/regionalPlanApprovals.constants'
import { ApprovalData, RegionalApprovalRow } from 'src/types/plan.interface'
import { isRegionalApproval } from 'src/utils/regionalApprovalType.util'

import { RootState } from '../stores/store'

// Optional throughout: the regional approval badge reads these from Bars, which
// is mounted app-wide and renders before the slices behind it are populated.
const approvalMessages = (state: RootState) => state.signalR?.approvalMessages
const fetchedApprovals = (state: RootState) => state.plans.approvals.data
const regionalApprovalQueue = (state: RootState) =>
  state.plans?.regionalApprovals?.data
const regionalPlanApproverRegions = (state: RootState) =>
  state.plans?.regionalPlanApproverRegions

// The API snapshot with SignalR *decisions* layered in front of it, so a live
// decision wins over the fetched record for the same plan when consumers
// find() the first match. Gates copying of core plans.
const selectApprovalsWithLiveDecisions = createSelector(
  [approvalMessages, fetchedApprovals],
  (messages, approvals): ApprovalData[] => [
    ...(messages?.filter(
      (approval: ApprovalData) => approval.approved !== undefined
    ) ?? []),
    ...approvals,
  ]
)

// The API snapshot with *undecided* SignalR requests layered in front of it,
// so a new copy request shows up in the approvals inbox before the next
// refetch. The inverse predicate of the selector above — the two had been
// hand-rolled in ModalHolder and PendingApprovals and were easy to confuse.
const selectApprovalsWithIncomingRequests = createSelector(
  [approvalMessages, fetchedApprovals],
  (messages, approvals): ApprovalData[] => [
    ...(messages?.filter(
      (approval: ApprovalData) => approval.approved === undefined
    ) ?? []),
    ...approvals,
  ]
)

/**
 * The regional plan copy requests awaiting the signed-in approver's decision,
 * and the new-elevation requests filed for their awareness, with live SignalR
 * traffic merged over the last fetched queue (LTGC-5711).
 *
 * The fetched array is only ever written by thunk fulfillments, so without this
 * merge neither the queue page nor its badge could move until something
 * refetched — a request that arrived while the approver was already looking at
 * the page stayed invisible until a reload, even though the broadcast had been
 * received and stored. The hub broadcasts every division's requests to every
 * client, so only the business regions this approver actually owns are
 * included; an approver whose regions have not loaded yet sees none rather than
 * all of them.
 *
 * Decided requests are NOT dropped. The queue endpoint returns only pending
 * ones, but deciding a request upserts the decided document into this array
 * before the refetch lands, and the page deliberately keeps showing that row
 * with its new status and its decision menu disabled. Filtering here would make
 * it vanish mid-interaction instead.
 */
const selectRegionalApprovalQueue = createSelector(
  [approvalMessages, regionalApprovalQueue, regionalPlanApproverRegions],
  (messages, queue, regions): RegionalApprovalRow[] => {
    const ownedRegions = new Set(
      (regions ?? []).map((region) => region.toLowerCase())
    )
    const approvalsById = new Map<string, RegionalApprovalRow>()

    queue?.forEach((approval) => approvalsById.set(approval.id, approval))

    // Live messages win over the snapshot for the same document, which also
    // keeps _etag current for the decision call.
    messages?.filter(isRegionalApproval).forEach((approval) => {
      if (
        ownedRegions.has(String(approval.businessRegion ?? '').toLowerCase())
      ) {
        approvalsById.set(approval.id, approval)
      }
    })

    return Array.from(approvalsById.values())
  }
)

/**
 * The badge counts only what still needs attention — copy requests awaiting a
 * decision and new-elevation requests not yet fulfilled — so a row left on the
 * page after being decided does not keep inflating it.
 */
const selectPendingRegionalApprovalCount = createSelector(
  [selectRegionalApprovalQueue],
  (approvals): number =>
    approvals.filter(
      (approval) => approval.status === RegionalPlanApprovalStatus.PENDING
    ).length
)

const useApprovalsWithLiveDecisions = () =>
  useSelector(selectApprovalsWithLiveDecisions)

const useApprovalsWithIncomingRequests = () =>
  useSelector(selectApprovalsWithIncomingRequests)

const useRegionalApprovalQueue = (): RegionalApprovalRow[] =>
  useSelector(selectRegionalApprovalQueue)

const usePendingRegionalApprovalCount = (): number =>
  useSelector(selectPendingRegionalApprovalCount)

export {
  useApprovalsWithIncomingRequests,
  useApprovalsWithLiveDecisions,
  usePendingRegionalApprovalCount,
  useRegionalApprovalQueue,
}
